import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Spin, Empty, message, Button, Modal, Form, Input, InputNumber, Select, Switch, AutoComplete } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined, BankOutlined, ReloadOutlined, SettingOutlined, SwapOutlined, CheckCircleOutlined, DisconnectOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import CustomerBandsViewer from '../components/CustomerBandsViewer';
import MemberSelector from '../components/MemberSelector';
import CustomerSelector from '../components/CustomerSelector';
import BandCardGrid from '../components/BandCardGrid';
import mqtt from 'mqtt';
import dayjs from 'dayjs';

// 主题定义
const themes = {
  default: {
    name: '默认主题',
    backgroundColor: '#f5f5f5',
    cardBackground: '#ffffff',
    cardBorder: '#f0f0f0',
    textPrimary: '#333333',
    textSecondary: '#666666',
    textMuted: '#999999',
    accentColor: '#1890ff',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: 'green',
    heartRateNormal: 'orange',
    heartRateHigh: 'red'
  },
  dark: {
    name: '深色主题',
    backgroundColor: '#1a1a1a',
    cardBackground: '#2a2a2a',
    cardBorder: '#404040',
    textPrimary: '#ffffff',
    textSecondary: '#cccccc',
    textMuted: '#888888',
    accentColor: '#40a9ff',
    onlineColor: '#73d13d',
    offlineColor: '#595959',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  blue: {
    name: '蓝色主题',
    backgroundColor: '#e6f7ff',
    cardBackground: '#ffffff',
    cardBorder: '#bae7ff',
    textPrimary: '#003a8c',
    textSecondary: '#0050b3',
    textMuted: '#69c0ff',
    accentColor: '#1890ff',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  green: {
    name: '绿色主题',
    backgroundColor: '#f6ffed',
    cardBackground: '#ffffff',
    cardBorder: '#b7eb8f',
    textPrimary: '#135200',
    textSecondary: '#237804',
    textMuted: '#52c41a',
    accentColor: '#52c41a',
    onlineColor: '#52c41a',
    offlineColor: '#a6a0a0',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  },
  cyber: {
    name: '赛博朋克',
    backgroundColor: '#0a1929',
    cardBackground: '#132f4c',
    cardBorder: '#1e4976',
    textPrimary: '#e0f2fe',
    textSecondary: '#7dd3fc',
    textMuted: '#38bdf8',
    accentColor: '#0ea5e9',
    onlineColor: '#52c41a',
    offlineColor: '#94a3b8',
    heartRateLow: '#52c41a',
    heartRateNormal: '#faad14',
    heartRateHigh: '#f5222d'
  }
};

const VenueDashboard = ({ user }) => {
  const {apiClient} = useAuth();
  const navigate = useNavigate();
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bindModalVisible, setBindModalVisible] = useState(false);
  const [bindMode, setBindMode] = useState('bind'); // 'bind' or 'switch'
  const [currentBand, setCurrentBand] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [bindForm] = Form.useForm();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dashboardRef = useRef(null);
  const [customerSelectorVisible, setCustomerSelectorVisible] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    // 使用用户默认校区
    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      return savedUser?.customerId || undefined;
    } catch (err) {
      console.warn('解析用户信息失败:', err);
      return undefined;
    }
  });
  const [selectedCustomerName, setSelectedCustomerName] = useState(() => {
    // 使用用户默认校区名称
    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      return savedUser?.customerName || '校区运动手环';
    } catch (err) {
      console.warn('解析用户信息失败:', err);
      return '校区运动手环';
    }
  });

  // 主题状态
  const [currentTheme, setCurrentTheme] = useState('default');

  // 展示课程状态
  const [showCourses, setShowCourses] = useState(false);
  
  // 枚举配置缓存
  const [bandTVConfig, setBandTVConfig] = useState({});
  
  // 枚举配置ID缓存（用于删除）
  const bandTVConfigIdsRef = useRef({});

  // 课程数据缓存
  const [coursesCache, setCoursesCache] = useState({});

  // MQTT 相关状态
  const [mqttData, setMqttData] = useState(null);
  const [debugMessages, setDebugMessages] = useState([]); // 调试消息数组
  const [mqttConnected, setMqttConnected] = useState(false);
  const [mqttStatus, setMqttStatus] = useState('未连接'); // 详细连接状态
  const mqttClientRef = useRef(null);
  
  // 当前系统时间状态
  const [currentTime, setCurrentTime] = useState(new Date());

  // 在线状态缓存，用于平滑状态切换
  const [onlineStatusCache, setOnlineStatusCache] = useState({});
  
  // 心率变化计数缓存
  const [heartRateChangeCache, setHeartRateChangeCache] = useState({});
  
  // 手环状态缓存
  const [bandStatuses, setBandStatuses] = useState({});
  
  // 使用 ref 来跟踪需要保存的最后在线数据，避免在渲染中调用 setState
  const pendingLastOnlineDataRef = useRef({});
  
  // 告警声音播放状态缓存，避免重复播放
  const [alarmSoundCache, setAlarmSoundCache] = useState({});
  
  // 手环激活状态缓存，记录已经自动展示过的手环，避免重复更新
  const activatedBandsRef = useRef(new Set());
  
  // 使用ref存储bands，确保WebSocket回调中能获取最新值
  const bandsRef = useRef(bands);
  useEffect(() => {
    bandsRef.current = bands;
  }, [bands]);
  
  // 心率告警阈值状态
  const [heartRateThreshold, setHeartRateThreshold] = useState(200);
  
  // 手环昵称枚举标签选项
  const [bandNickNameOptions, setBandNickNameOptions] = useState([]);
  
  // 手环昵称输入值（用于控制快速选择标签的显示）
  const [bandNickNameInput, setBandNickNameInput] = useState('');
  
  // 主题下拉菜单显示状态
  const [showThemeDropdown, setShowThemeDropdown] = useState(false);
  
  // 工具栏显示状态（齿轮图标控制）
  const [showToolbar, setShowToolbar] = useState(false);
  
  // 维护弹窗显示状态
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);

  // 安全更新在线状态缓存的函数
  const updateOnlineStatusCache = useCallback((bandEnumCode, isOnline, timestamp) => {
    setOnlineStatusCache(prev => {
      const newCache = { ...prev };
      if (isOnline && timestamp) {
        newCache[bandEnumCode] = {
          lastOnlineTime: timestamp,
          isCurrentlyOnline: true,
          lastUpdateTime: Date.now()
        };
      }
      return newCache;
    });
  }, []);
  
  // 播放告警声音的函数
  const playAlarmSound = useCallback((bandEnumCode) => {
    // 检查是否已经在播放告警，避免重复播放
    const lastAlarmTime = alarmSoundCache[bandEnumCode];
    const currentTime = Date.now();
    
    // 如果距离上次告警不到30秒，则不重复播放
    if (lastAlarmTime && (currentTime - lastAlarmTime) < 30000) {
      return;
    }
    
    // 更新告警时间缓存
    setAlarmSoundCache(prev => ({
      ...prev,
      [bandEnumCode]: currentTime
    }));
    
    try {
      // 创建音频上下文
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      // 连接节点
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // 设置音调参数
      oscillator.type = 'sine'; // 正弦波
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz频率
      
      // 设置音量渐变（淡入淡出效果）
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01); // 淡入
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.2); // 保持
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3); // 淡出
      
      // 开始播放
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      console.log(`播放心率告警声音: 手环 ${bandEnumCode}`);
    } catch (error) {
      console.error('播放告警声音失败:', error);
    }
  }, [alarmSoundCache]);
  
  // 监听MQTT数据变化，安全更新缓存
  useEffect(() => {
    if (mqttData?.devices && Array.isArray(mqttData.devices)) {
      mqttData.devices.forEach(device => {
        if (device.timestamp) {
          updateOnlineStatusCache(device.device_id, true, device.timestamp);
          
          // 同时更新数据缓存和时间缓存
          setOnlineStatusCache(prev => ({
            ...prev,
            [`data_${device.device_id}`]: {
              ...device,
              lastDataUpdateTime: Date.now()
            },
            [`time_${device.device_id}`]: {
              lastTimestamp: device.timestamp,
              lastTimeUpdateTime: Date.now()
            }
          }));
        }
      });
    }
  }, [mqttData, updateOnlineStatusCache]);
  
  // 定时清理过期缓存
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineStatusCache(prev => {
        const newCache = { ...prev };
        const currentTime = Date.now();
        
        // 清理超过5分钟的过期缓存
        Object.keys(newCache).forEach(key => {
          const cacheEntry = newCache[key];
          if (cacheEntry && cacheEntry.lastOnlineTime) {
            const timeSinceLastOnline = currentTime - cacheEntry.lastOnlineTime;
            if (timeSinceLastOnline > 300000) { // 5分钟
              delete newCache[key];
            }
          }
        });
        
        return newCache;
      });
      
      // 同时清理告警声音缓存（超过1分钟的记录）
      setAlarmSoundCache(prev => {
        const newCache = { ...prev };
        const currentTime = Date.now();
        Object.keys(newCache).forEach(key => {
          const alarmTime = newCache[key];
          if (alarmTime && (currentTime - alarmTime) > 60000) { // 1分钟
            delete newCache[key];
          }
        });
        return newCache;
      });
    }, 30000); // 每30秒检查一次
    
    return () => clearInterval(interval);
  }, []);
  
  // 点击外部关闭主题下拉菜单
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showThemeDropdown) {
        setShowThemeDropdown(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showThemeDropdown]);
  
  // 获取BAND_TV枚举配置
  const fetchBandTVConfig = useCallback(async () => {
    if (!apiClient || !selectedCustomerId) return;
    try {
      const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'BAND_TV',
        colName: String(selectedCustomerId)
      });
      
      if (res.data?.code === 200) {
        let rawData = res.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        const configList = Array.isArray(rawData) ? rawData : [];
        const configMap = {};
        const configIds = {};
        
        configList.forEach(item => {
          configMap[item.enumCode] = item.enumName;
          configIds[item.enumCode] = item.configId;
        });
        
        setBandTVConfig(configMap);
        bandTVConfigIdsRef.current = configIds;
        
        // 初始化状态
        if (configMap.venueDashboardTheme && themes[configMap.venueDashboardTheme]) {
          setCurrentTheme(configMap.venueDashboardTheme);
        }
        if (configMap.venueDashboardShowCourses !== undefined) {
          setShowCourses(configMap.venueDashboardShowCourses === 'true');
        }
        if (configMap.venueDashboardHeartRateThreshold) {
          const threshold = parseInt(configMap.venueDashboardHeartRateThreshold);
          if (!isNaN(threshold)) {
            setHeartRateThreshold(threshold);
          }
        }
      }
    } catch (error) {
      console.error('获取BAND_TV枚举配置失败:', error);
    }
  }, [apiClient, selectedCustomerId]);
  
  // 保存BAND_TV枚举配置（先删除再保存）
  const saveBandTVConfig = useCallback(async (enumCode, enumName) => {
    if (!apiClient || !selectedCustomerId) return;
    try {
      // 先查询获取最新的configId（确保能正确删除）
      const listRes = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'BAND_TV',
        colName: String(selectedCustomerId)
      });
      
      let existingConfigId = null;
      if (listRes.data?.code === 200) {
        let rawData = listRes.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        const configList = Array.isArray(rawData) ? rawData : [];
        const existingItem = configList.find(item => item.enumCode === enumCode);
        if (existingItem) {
          existingConfigId = existingItem.configId;
        }
      }
      
      // 先删除原有配置
      if (existingConfigId) {
        console.log(`[BAND_TV] 删除原有配置: ${enumCode}, configId: ${existingConfigId}`);
        const deleteRes = await apiClient.delete(`/portal-server-prod/sys/enumconfig/v1/delete/${existingConfigId}`);
        console.log(`[BAND_TV] 删除结果:`, deleteRes.data);
      } else {
        console.log(`[BAND_TV] 没有找到原有配置: ${enumCode}`);
      }
      
      // 保存新配置
      const payload = {
        tableName: 'BAND_TV',
        colName: String(selectedCustomerId),
        enumCode: enumCode,
        enumName: String(enumName),
        remark: '',
        showSort: 0,
        state: 1,
        parentEnumCode: 0,
        configId: 0
      };
      
      console.log(`[BAND_TV] 保存新配置:`, payload);
      const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      console.log(`[BAND_TV] 保存结果:`, res.data);
      
      if (res.data?.code === 200) {
        // 更新本地缓存
        setBandTVConfig(prev => ({ ...prev, [enumCode]: String(enumName) }));
        if (res.data.data?.configId) {
          bandTVConfigIdsRef.current[enumCode] = res.data.data.configId;
        }
      }
    } catch (error) {
      console.error('[BAND_TV] 保存枚举配置失败:', error);
    }
  }, [apiClient, selectedCustomerId]);

  // 连接MQTT
  useEffect(() => {
    // 获取账号密码
    let mqttUserName = '';
    let mqttPassword = '';
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      mqttUserName = user.mqttUserName;
      mqttPassword = user.mqttPassword;
    } catch (e) {}

    // 连接MQTT - 优化长连接配置
    const client = mqtt.connect('wss://taller.wecarebaby.cn/mqtt-subscriber/', {
      username: mqttUserName,
      password: mqttPassword,
      reconnectPeriod: 3000, // 缩短重连间隔到3秒
      connectTimeout: 30 * 1000,
      clean: false, // 改为false，保持会话状态
      clientId: 'portal_' + Math.random().toString(16).substr(2, 8),
      keepalive: 60, // 60秒心跳包
      resubscribe: true, // 重连后自动重新订阅
      will: { // 遗嘱消息
        topic: 'venue/status',
        payload: JSON.stringify({
          clientId: 'portal_' + Math.random().toString(16).substr(2, 8),
          status: 'disconnected',
          timestamp: Date.now()
        }),
        qos: 1,
        retain: false
      }
    });
    mqttClientRef.current = client;

    client.on('connect', () => {
      setMqttConnected(true);
      setMqttStatus('已连接');
      console.log('MQTT连接成功');
      // 订阅主题 - 根据手环编号动态订阅
      // 首先订阅通用状态主题
      client.subscribe('venue/status', { qos: 1 });
          
      // 只订阅指定的topic
      client.subscribe('up/wecareanalysis/scan', { qos: 1 });
      
      // 订阅刷新topic（使用当前机构ID）
      if (selectedCustomerId) {
        const refreshTopic = `up/${selectedCustomerId}/bandTV_refresh`;
        client.subscribe(refreshTopic, { qos: 1 });
        console.log(`订阅刷新topic: ${refreshTopic}`);
        
        // 订阅隐藏空闲手环topic
        const hideIdleTopic = `up/${selectedCustomerId}/bandTV_hideIdle`;
        client.subscribe(hideIdleTopic, { qos: 1 });
        console.log(`订阅隐藏空闲手环topic: ${hideIdleTopic}`);
      }
    });

    client.on('reconnect', () => {
      setMqttConnected(false);
      setMqttStatus('重连中...');
      console.log('MQTT正在重连...');
    });

    client.on('close', () => {
      setMqttConnected(false);
      setMqttStatus('连接关闭');
      console.log('MQTT连接关闭');
    });

    client.on('offline', () => {
      setMqttConnected(false);
      setMqttStatus('离线');
      console.log('MQTT离线');
    });

    client.on('error', (err) => {
      setMqttConnected(false);
      setMqttStatus('连接错误');
      console.error('MQTT连接错误:', err);
      // 错误后尝试重连
      setTimeout(() => {
        if (client && !client.connected) {
          console.log('尝试重新连接MQTT...');
          client.reconnect();
        }
      }, 5000); // 5秒后重试
    });

    client.on('message', (topic, message) => {
      try {
        const data = JSON.parse(message.toString());
        setMqttData(data);
        
        // 添加到调试消息数组（最多保留50条）
        setDebugMessages(prev => [
          {
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
            topic: topic,
            data: data,
            processed: topic.startsWith('up/') && topic.endsWith('/scan') ? '已处理为手环数据' : '未处理'
          },
          ...prev.slice(0, 49)
        ]);

        // 处理不同主题的消息
        if (topic === 'venue/status') {
        } else if (topic === `up/${selectedCustomerId}/bandTV_refresh`) {
          // 收到刷新指令，AJAX方式无感刷新配置
          console.log('[MQTT] 收到刷新指令，无感刷新配置');
          fetchBands(true);
          fetchBandTVConfig();
        } else if (topic === `up/${selectedCustomerId}/bandTV_hideIdle`) {
          // 收到隐藏空闲手环指令，自动执行隐藏空闲手环功能
          console.log('[MQTT] 收到隐藏空闲手环指令，自动执行隐藏空闲手环');
          handleHideIdleBands();
        } else if (topic === 'up/wecareanalysis/scan') {

          // 解析data.hrList中的手环数据
          try {
            const hrList = data.data?.hrList || [];
            
            hrList.forEach(hrItem => {
              // 确保hrItem是对象且包含必要的字段
              if (hrItem && typeof hrItem === 'object' && hrItem.mac) {
                // 构建实时数据对象
                const realtimeData = {
                  device_id: hrItem.mac,
                  heart_rate: hrItem.hr || 0,
                  calories: hrItem.calorie || 0,
                  max_heart_rate: hrItem.hrMax || 0,
                  exercise_duration: hrItem.seconds || 0,
                  timestamp: hrItem.time || Date.now()
                };
                
                // 检测心率是否过高（使用可配置阈值）
                if (realtimeData.heart_rate >= heartRateThreshold) {
                  console.log(`检测到心率过高: ${realtimeData.heart_rate} bpm, 手环: ${hrItem.mac}, 阈值: ${heartRateThreshold}`);
                  playAlarmSound(hrItem.mac);
                }
                
                // 更新心率变化计数器
                if (realtimeData.heart_rate === 0) {
                  // 心率为0，递增计数器
                  setHeartRateChangeCache(prev => ({
                    ...prev,
                    [hrItem.mac]: {
                      zeroCount: (prev[hrItem.mac]?.zeroCount || 0) + 1,
                      lastHeartRate: 0
                    }
                  }));
                } else {
                  // 心率非0，重置计数器
                  setHeartRateChangeCache(prev => {
                    const newCache = { ...prev };
                    delete newCache[hrItem.mac];
                    return newCache;
                  });
                }
                
                // 更新mqttData状态
                setMqttData(prev => {
                  if (!prev) return { devices: [realtimeData] };
                  const existingIndex = Array.isArray(prev.devices) ? prev.devices.findIndex(d => d.device_id === hrItem.mac) : -1;
                  if (existingIndex >= 0) {
                    const newDevices = Array.isArray(prev.devices) ? [...prev.devices] : [];
                    if (existingIndex >= 0 && existingIndex < newDevices.length) {
                      newDevices[existingIndex] = realtimeData;
                    } else {
                      newDevices.push(realtimeData);
                    }
                    return { ...prev, devices: newDevices };
                  } else {
                    const newDevices = Array.isArray(prev.devices) ? [...prev.devices] : [];
                    newDevices.push(realtimeData);
                    return { ...prev, devices: newDevices };
                  }
                });

                // 检查是否需要自动更新showOnDashboard为true
                // 当手环有有效心率数据且showOnDashboard为false且绑定了会员时，自动更新为true
                // 但只更新一次，使用ref记录已激活的手环避免重复更新
                if (realtimeData.heart_rate > 0) {
                  // 使用 bandsRef 获取最新状态，避免 useEffect 闭包问题
                  const band = bandsRef.current.find(b => b.enumCode === hrItem.mac);
                  if (band && band.showOnDashboard === false && band.boundMember && !activatedBandsRef.current.has(hrItem.mac)) {
                    // 标记为已激活
                    activatedBandsRef.current.add(hrItem.mac);
                    // 使用setTimeout避免在渲染过程中调用setState
                    setTimeout(() => {
                      autoShowBandOnDashboard(band);
                    }, 0);
                  }
                }
              }
            });
          } catch (e) {
            console.error('解析hrList数据失败:', e, data);
          }
        }
      } catch (e) {
        console.error('解析MQTT消息失败:', e);
        // 记录解析失败的消息
        setDebugMessages(prev => [
          {
            id: Date.now(),
            timestamp: new Date().toLocaleString(),
            topic: topic,
            data: message.toString(),
            error: '解析失败'
          },
          ...prev.slice(0, 49)
        ]);
      }
    });

    // 页面卸载时的清理函数
    const handleBeforeUnload = () => {
      if (client && client.connected) {
        console.log('页面即将关闭，断开MQTT连接');
        client.end();
      }
    };
    
    // 监听页面关闭事件
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // 监听页面可见性变化
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('页面隐藏');
      } else {
        console.log('页面显示');
        // 页面重新显示时检查连接状态
        if (client && !client.connected) {
          console.log('页面恢复，尝试重连MQTT');
          client.reconnect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      // 组件卸载时清理
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (client) {
        console.log('组件卸载，断开MQTT连接');
        client.end();
      }
    };
  }, []);

  // 获取校区列表
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!apiClient) return;

      try {
        const response = await apiClient.get('/sports-base-server-prod/myPage/searchCustomer', {
          params: { pageNum: 1, pageSize: 999 }
        });

        if (response.data.result === '1') {
          const fullCustomerList = response.data.customerList || [];

          // 根据权限过滤校区列表
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (!currentUser) {
            setCustomers(fullCustomerList);
            return;
          }

          let filteredCustomers = [];
          if (currentUser.role === 'R00001') {
            // 角色为 R00001 时，可以看到所有校区
            filteredCustomers = fullCustomerList;
          } else {
            // 检查当前用户是否在任何校区的 phoneDecimal 中，或者 customerId 是否匹配
            filteredCustomers = fullCustomerList.filter(customer => {
              // 检查 customerId 是否匹配当前用户
              if (customer.customerId === currentUser.customerId) {
                return true;
              }

              // 检查当前用户的各种可能标识是否在 phoneDecimal 中 (精确匹配)
              if (customer.phoneDecimal) {
                const phoneDecimals = customer.phoneDecimal.split(';').map(item => item.trim()).filter(item => item);

                // 检查多种可能的用户标识
                const userIdentifiers = [
                  currentUser.userName,     // 用户名
                  currentUser.name,         // 姓名
                  currentUser.userId,       // 用户ID
                  currentUser.username,     // 登录用户名
                ].filter(id => id); // 过滤掉undefined或null值

                // 检查是否有任何一个用户标识存在于phoneDecimal中
                return userIdentifiers.some(identifier =>
                  phoneDecimals.includes(identifier)
                );
              }

              return false;
            });
          }

          setCustomers(filteredCustomers);
        }
      } catch (error) {
        console.error('获取校区列表失败:', error);
      }
    };

    fetchCustomers();
  }, [apiClient]);

  // 获取当前用户角色
  const getCurrentUserRole = () => {
    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      return savedUser?.role || '';
    } catch (err) {
      return '';
    }
  };

  // 处理校区切换
  const handleCustomerChange = (customerId, customerName) => {
    setSelectedCustomerId(customerId);
    setSelectedCustomerName(customerName || '校区运动手环');
    // 更新localStorage中的默认校区
    try {
      const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
      savedUser.customerId = customerId;
      savedUser.customerName = customerName;
      localStorage.setItem('user', JSON.stringify(savedUser));
    } catch (err) {
      console.warn('更新用户信息失败:', err);
    }
  };

  // 检测是否在WebView环境中
  const isWebView = () => {
    const userAgent = navigator.userAgent || '';
    return userAgent.includes('WeCareTaller') ||
        /wv|WebView/i.test(userAgent) ||
        (window.Android !== undefined);
  };

  // 检测是否是WcNative WebView
  const isWcNativeWebView = () => {
    return window.__WcNative !== undefined && 
           typeof window.__WcNative.enterFullscreen === 'function' &&
           typeof window.__WcNative.exitFullscreen === 'function';
  };

// 处理全屏切换
  const toggleFullscreen = () => {
    const newFullscreenState = !isFullscreen;

    // 控制页面外部元素显隐（左侧菜单和顶部区域）
    try {
      // 获取Dashboard页面的外部容器元素
      const rootElement = document.getElementById('root');
      const siderElement = document.querySelector('.dashboard-layout > .ant-layout-sider');
      const headerElement = document.querySelector('.dashboard-header');

      if (newFullscreenState) {
        // 进入全屏：隐藏外部元素
        if (siderElement) siderElement.style.display = 'none';
        if (headerElement) headerElement.style.display = 'none';
        // 调整内容区域样式
        const contentElement = document.querySelector('.dashboard-content');
        if (contentElement) {
          contentElement.style.padding = '0';
          contentElement.style.overflow = 'hidden';
        }
      } else {
        // 退出全屏：恢复外部元素
        if (siderElement) siderElement.style.display = '';
        if (headerElement) headerElement.style.display = '';
        const contentElement = document.querySelector('.dashboard-content');
        if (contentElement) {
          contentElement.style.padding = '';
          contentElement.style.overflow = '';
        }
      }
    } catch (err) {
      console.warn('控制外部元素显隐失败:', err);
    }

    // WcNative WebView环境下使用原生接口
    if (isWcNativeWebView()) {
      if (newFullscreenState) {
        // 进入全屏 - 调用Android原生接口隐藏系统导航栏/状态栏
        try {
          window.__WcNative.enterFullscreen();
        } catch (err) {
          console.warn('WcNative fullscreen request failed:', err);
        }
      } else {
        // 退出全屏 - 调用Android原生接口
        try {
          window.__WcNative.exitFullscreen();
        } catch (err) {
          console.warn('WcNative exit fullscreen failed:', err);
        }
      }
      // 切换全屏状态（控制左侧菜单和顶部标签的显示/隐藏）
      setIsFullscreen(newFullscreenState);
      return;
    }

    // 普通WebView或浏览器环境下使用标准Fullscreen API
    const targetElement = isWebView() ? document.documentElement : dashboardRef.current;

    if (newFullscreenState) {
      // 进入全屏
      const requestMethod =
          targetElement.requestFullscreen ||
          targetElement.webkitRequestFullscreen ||
          targetElement.mozRequestFullScreen ||
          targetElement.msRequestFullscreen;

      if (requestMethod) {
        requestMethod.call(targetElement).catch(err => {
          console.warn('Fullscreen request failed:', err);
          // 降级：尝试使用body
          if (document.body.requestFullscreen) {
            document.body.requestFullscreen().catch(e => console.warn('Body fullscreen failed:', e));
          }
        });
      }
      setIsFullscreen(true);
    } else {
      // 退出全屏
      const exitMethod =
          document.exitFullscreen ||
          document.webkitExitFullscreen ||
          document.mozCancelFullScreen ||
          document.msExitFullscreen;

      if (exitMethod) {
        exitMethod.call(document).catch(err => console.warn('Exit fullscreen failed:', err));
      }
      setIsFullscreen(false);
    }
    }
  
  // 监听全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = 
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      
      setIsFullscreen(!!fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 获取手环数据
  const fetchBands = useCallback(async () => {
    if (!selectedCustomerId || !apiClient) {
      setBands([]);
      return;
    }

    setLoading(true);
    try {
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'CUSTOMER_BAND',
        colName: selectedCustomerId
      });

      if (resp.data?.code === 200) {
        let raw = resp.data.data;
        if (typeof raw === 'string') {
          raw = JSON.parse(raw);
        }
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list
            .filter(item => !item.colName || String(item.colName) === String(selectedCustomerId))
            .map(item => {
              // 解析会员绑定信息、展示设置和所属类型
              let boundMember = null;
              let showOnDashboard = false; // 默认不展示，必须显式设置为true才展示
              let belong = 'venue'; // 默认为场馆
              if (item.remark) {
                try {
                  const remarkData = JSON.parse(item.remark);
                  // 提取会员信息（id, babyName, photoAddress）
                  if (remarkData.id) {
                    boundMember = {
                      id: remarkData.id,
                      babyName: remarkData.babyName,
                      photoAddress: remarkData.photoAddress
                    };
                  }
                  // 提取展示设置，只有显式为true时才展示
                  showOnDashboard = remarkData.showOnDashboard === true;
                  // 提取所属类型，默认为venue
                  belong = remarkData.belong === 'person' ? 'person' : 'venue';
                } catch (e) {
                  console.warn('解析会员绑定信息失败:', e);
                  boundMember = null;
                }
              }

              return {
                configId: item.configId,
                enumCode: item.enumCode,
                enumName: item.enumName,
                remark: item.remark || '',
                boundMember: boundMember,
                showOnDashboard: showOnDashboard,
                belong: belong, // 所属类型：venue(场馆)或person(个人)
                showSort: item.showSort ?? 0,
                state: item.state ?? 1
              };
            })
            .sort((a, b) => (a.showSort ?? 0) - (b.showSort ?? 0));
        setBands(normalized);
        
        // 清理 activatedBandsRef 中已被隐藏的手环，允许它们下次被自动展示
        normalized.forEach(band => {
          if (band.showOnDashboard === false && activatedBandsRef.current.has(band.enumCode)) {
            activatedBandsRef.current.delete(band.enumCode);
            console.log(`[fetchBands] 清理已隐藏手环的激活记录: ${band.enumCode}`);
          }
        });
      } else {
        message.error(resp.data?.msg || '获取手环列表失败');
      }
    } catch (err) {
      console.error('获取手环列表异常:', err);
      message.error('获取手环列表失败');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId, apiClient]);

  useEffect(() => {
    if (selectedCustomerId && apiClient) {
      fetchBands();
    } else if (!selectedCustomerId) {
      setBands([]);
    }
  }, [selectedCustomerId, apiClient, fetchBands]);
  
  // 获取手环昵称枚举标签
  const fetchBandNickNameOptions = useCallback(async () => {
    if (!apiClient) return;
    try {
      const res = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'BAND_NICK_NAME',
        colName: 'BAND_NICK_NAME'
      });
      
      if (res.data?.code === 200) {
        let rawData = res.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        const options = (rawData || []).map(item => ({
          label: item.enumName,
          value: item.enumName
        }));
        setBandNickNameOptions(options);
      }
    } catch (error) {
      console.error('获取手环昵称枚举标签失败:', error);
    }
  }, [apiClient]);
  
  // 组件挂载时获取枚举标签
  useEffect(() => {
    fetchBandNickNameOptions();
  }, [fetchBandNickNameOptions]);

  // 更新当前系统时间
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // 获取BAND_TV配置
  useEffect(() => {
    if (selectedCustomerId && apiClient) {
      fetchBandTVConfig();
    }
  }, [selectedCustomerId, apiClient, fetchBandTVConfig]);

  // 获取本日课程 - 提前声明以避免 useEffect 中访问顺序问题
  const fetchTodayCourses = useCallback(async (babyId) => {
    if (!apiClient || !babyId) return null;

    try {
      const today = dayjs().format('YYYY-MM-DD');
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/prescription/pageList', {
        babyId: babyId,
        pageNo: 1,
        pageSize: 999,
        searchDate: today,
        courseKbn: "线下课程"
      });

      if (response.data.code === 200) {
        return response.data.data?.list || [];
      }
    } catch (error) {
      console.error('获取本日课程失败:', error);
    }
    return null;
  }, [apiClient]);

  // 更新课程缓存 - 提前声明以避免 useEffect 中访问顺序问题
  const updateCoursesCache = useCallback(async (babyId) => {
    const courses = await fetchTodayCourses(babyId);
    if (courses) {
      setCoursesCache(prev => ({
        ...prev,
        [babyId]: courses
      }));
    }
  }, [fetchTodayCourses]);

  // 将 ref 中的最后在线数据同步到状态中
  useEffect(() => {
    const syncLastOnlineData = () => {
      const pendingData = pendingLastOnlineDataRef.current;
      const statusChanges = statusChangeRef.current;
      const bandCodes = Object.keys(pendingData);
      const statusBandCodes = Object.keys(statusChanges);
      
      if (bandCodes.length > 0 || statusBandCodes.length > 0) {
        setBandStatuses(prev => {
          const newStatuses = { ...prev };
          
          // 同步最后在线数据
          bandCodes.forEach(bandCode => {
            newStatuses[bandCode] = {
              ...newStatuses[bandCode],
              lastOnlineData: pendingData[bandCode]
            };
          });
          
          // 同步状态变化
          statusBandCodes.forEach(bandCode => {
            const change = statusChanges[bandCode];
            newStatuses[bandCode] = {
              ...newStatuses[bandCode],
              status: change.newStatus
            };
          });
          
          return newStatuses;
        });
        
        // 处理状态变化副作用（获取课程）
        statusBandCodes.forEach(bandCode => {
          const change = statusChanges[bandCode];
          if (change.currentStatus === 'idle' && change.newStatus === 'online' && showCourses && change.hasBoundMember) {
            updateCoursesCache(change.memberId);
          }
        });
        
        // 清空 ref
        pendingLastOnlineDataRef.current = {};
        statusChangeRef.current = {};
      }
    };

    // 使用 setInterval 定期同步数据
    const timer = setInterval(syncLastOnlineData, 500);
    return () => clearInterval(timer);
  }, [showCourses, updateCoursesCache]);

  // 处理绑定手环
  const handleBindBand = (band) => {
    setCurrentBand(band);
    setSelectedMember(null);
    bindForm.resetFields();
    bindForm.setFieldsValue({
      enumCode: band.enumCode,
      enumName: band.enumName
    });
    setBindModalVisible(true);
  };

  // 处理取消绑定
  const handleUnbindBand = async (band) => {
    if (!apiClient || !selectedCustomerId) return;

    try {
      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: band.enumCode,
        enumName: band.enumName,
        remark: '', // 清空remark字段
        showSort: band.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: band.configId
      };

      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('取消绑定成功');
        fetchBands(); // 重新加载数据
      } else {
        message.error(resp.data?.msg || '取消绑定失败');
      }
    } catch (err) {
      console.error('取消绑定异常:', err);
      message.error('取消绑定失败');
    }
  };

  // 处理卡片点击
  const handleCardClick = (band) => {
    setCurrentBand(band);
    bindForm.resetFields();
    bindForm.setFieldsValue({
      enumCode: band.enumCode,
      enumName: band.enumName
    });
    // 初始化手环昵称输入值
    setBandNickNameInput(band.enumName || '');

    if (band.boundMember) {
      // 已绑定：显示切换会员弹窗，并预选中当前会员
      setSelectedMember(band.boundMember);
      
      // 解析当前remark中的showOnDashboard设置（如果有）
      let existingData = {};
      if (band.remark) {
        try {
          existingData = JSON.parse(band.remark);
        } catch (e) {
          // 解析失败则忽略
        }
      }
      
      // 合并会员信息和原有设置
      bindForm.setFieldsValue({
        remark: JSON.stringify({
          ...existingData,
          id: band.boundMember.id,
          babyName: band.boundMember.babyName,
          photoAddress: band.boundMember.photoAddress
        })
      });
      setBindModalVisible(true);
      setBindMode('switch');
    } else {
      // 未绑定：显示绑定会员选项
      setSelectedMember(null);
      setBindModalVisible(true);
      setBindMode('bind');
    }
  };


  // 处理会员选择
  const handleMemberSelect = (member) => {
    setSelectedMember(member);
    
    // 获取当前remark中的showOnDashboard设置（如果有）
    const currentRemark = currentBand?.remark || '';
    let existingData = {};
    if (currentRemark) {
      try {
        existingData = JSON.parse(currentRemark);
      } catch (e) {
        // 解析失败则忽略
      }
    }
    
    // 合并会员信息和原有设置
    bindForm.setFieldsValue({
      remark: JSON.stringify({
        ...existingData,
        id: member.id,
        babyName: member.babyName,
        photoAddress: member.photoAddress
      })
    });
  };

  // 保存绑定
  const handleSaveBind = async () => {
    if (!currentBand || !apiClient || !selectedCustomerId) return;
    
    try {
      const values = await bindForm.validateFields();
      
      // 检查别名是否与其他记录重复（仅在修改了别名时）
      if (values.enumName !== currentBand.enumName) {
        const duplicateName = bands.some(
          item => item.enumName === values.enumName && item.configId !== currentBand.configId
        );
        if (duplicateName) {
          message.warning('手环昵称已存在，请更换别名');
          return;
        }
      }
      
      // 合并remark：保留原有的showOnDashboard设置，添加/更新会员信息
      let finalRemark = values.remark || '';
      if (finalRemark) {
        try {
          const remarkData = JSON.parse(finalRemark);
          // 确保showOnDashboard被保留（如果表单中没有的话）
          if (currentBand.remark) {
            try {
              const existingData = JSON.parse(currentBand.remark);
              if (existingData.showOnDashboard !== undefined && remarkData.showOnDashboard === undefined) {
                remarkData.showOnDashboard = existingData.showOnDashboard;
              }
            } catch (e) {
              // 解析失败则忽略
            }
          }
          finalRemark = JSON.stringify(remarkData);
        } catch (e) {
          // 解析失败则使用原值
        }
      }
      
      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: currentBand.enumCode, // 使用原有编号
        enumName: values.enumName, // 使用表单中的别名（可能已修改）
        remark: finalRemark,
        showSort: currentBand.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: currentBand.configId
      };
      
      const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
      if (resp.data?.code === 200) {
        message.success('保存成功');
        setBindModalVisible(false);
        setCurrentBand(null);
        setSelectedMember(null);
        setBandNickNameInput('');
        bindForm.resetFields();
        fetchBands(); // 重新加载数据
      } else {
        message.error(resp.data?.msg || '保存失败');
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      }
      console.error('绑定异常:', err);
      message.error('保存失败');
    }
  };

  // 取消绑定操作
  const handleCancelBind = () => {
    setBindModalVisible(false);
    setCurrentBand(null);
    setSelectedMember(null);
    setBandNickNameInput('');
    bindForm.resetFields();
  };

  // 处理隐藏手环（设置showOnDashboard为false）
  const handleHideBand = async (band) => {
    if (!apiClient || !selectedCustomerId) return;

    try {
      // 解析当前remark
      let remarkData = {};
      if (band.remark) {
        try {
          remarkData = JSON.parse(band.remark);
        } catch (e) {
          remarkData = { _originalRemark: band.remark };
        }
      }

      // 设置showOnDashboard为false
      remarkData.showOnDashboard = false;
      const newRemark = JSON.stringify(remarkData);

      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: band.enumCode,
        enumName: band.enumName,
        remark: newRemark,
        showSort: band.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: band.configId
      };

      // 静默发送ajax请求，不刷新页面
      apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload)
        .then(resp => {
          if (resp.data?.code === 200) {
            message.success('已隐藏该手环');
            // 只更新本地状态，不重新获取数据
            setBands(prevBands => 
              prevBands.map(b => 
                b.configId === band.configId 
                  ? { ...b, showOnDashboard: false, remark: newRemark }
                  : b
              )
            );
            // 从激活缓存中移除，允许下次心率数据再次激活
            activatedBandsRef.current.delete(band.enumCode);
          } else {
            message.error(resp.data?.msg || '隐藏失败');
          }
        })
        .catch(err => {
          console.error('隐藏手环异常:', err);
          message.error('隐藏失败');
        });
    } catch (err) {
      console.error('隐藏手环异常:', err);
      message.error('隐藏失败');
    }
  };

  // 自动展示手环到大屏（当接收到心率数据且showOnDashboard为false时调用）
  const autoShowBandOnDashboard = async (band) => {
    if (!apiClient || !selectedCustomerId) return;

    try {
      // 解析当前remark
      let remarkData = {};
      if (band.remark) {
        try {
          remarkData = JSON.parse(band.remark);
        } catch (e) {
          remarkData = { _originalRemark: band.remark };
        }
      }

      // 设置showOnDashboard为true
      remarkData.showOnDashboard = true;
      const newRemark = JSON.stringify(remarkData);

      const payload = {
        tableName: 'CUSTOMER_BAND',
        colName: String(selectedCustomerId),
        enumCode: band.enumCode,
        enumName: band.enumName,
        remark: newRemark,
        showSort: band.showSort ?? 0,
        state: 1,
        parentEnumCode: 0,
        configId: band.configId
      };

      // 静默发送ajax请求，不刷新页面
      console.log(`[API] 发送自动展示请求: ${band.enumCode}`);
      apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload)
        .then(resp => {
          if (resp.data?.code === 200) {
            console.log(`[API] 手环 ${band.enumCode} 已自动设置为展示状态`);
            // 只更新本地状态，不重新获取数据
            setBands(prevBands => 
              prevBands.map(b => 
                b.configId === band.configId 
                  ? { ...b, showOnDashboard: true, remark: newRemark }
                  : b
              )
            );
          } else {
            console.error(`[API] 自动展示手环 ${band.enumCode} 失败:`, resp.data?.msg);
            // 失败时从激活缓存中移除，允许下次重试
            activatedBandsRef.current.delete(band.enumCode);
          }
        })
        .catch(err => {
          console.error('[API] 自动展示手环异常:', err);
          // 失败时从激活缓存中移除，允许下次重试
          activatedBandsRef.current.delete(band.enumCode);
        });
    } catch (err) {
      console.error('自动展示手环异常:', err);
      activatedBandsRef.current.delete(band.enumCode);
    }
  };

  // 使用 ref 来跟踪状态变化，避免在渲染中调用 setState
  const statusChangeRef = useRef({});
  
  // 获取手环状态
  const getBandStatus = useCallback((band) => {
    const realtimeData = getBandRealtimeData(band.enumCode);

    // 检查心率连续零值计数
    const heartRateChangeInfo = heartRateChangeCache[band.enumCode];
    const isZeroCountReached = heartRateChangeInfo && heartRateChangeInfo.zeroCount >= 30;
    
    // 实时心率检查
    const hasValidHeartRate = realtimeData?.heart_rate > 0;
    
    // 获取当前状态
    const currentStatus = bandStatuses[band.enumCode]?.status || 'idle';

    let newStatus = currentStatus;

    if (hasValidHeartRate) {
      newStatus = 'online';
    } else if (isZeroCountReached) {
      newStatus = 'idle';
    }
    // else keep current

    // 记录状态变化到 ref，避免在渲染中调用 setState
    if (newStatus !== currentStatus) {
      statusChangeRef.current[band.enumCode] = {
        newStatus,
        currentStatus,
        hasBoundMember: !!band.boundMember,
        memberId: band.boundMember?.id
      };
    }

    return newStatus;
  }, [heartRateChangeCache, bandStatuses]);

  // 批量隐藏所有空闲状态的手环
  const handleHideIdleBands = useCallback(async (showEmptyMessage = true) => {
    if (!apiClient || !selectedCustomerId) return;
    
    // 获取所有空闲状态的手环（showOnDashboard=true 且当前状态为idle）
    // 使用 bandsRef.current 获取最新状态，避免闭包问题
    const currentBands = bandsRef.current;
    const idleBands = currentBands.filter(band => {
      if (band.showOnDashboard === false) return false;
      if (!band.boundMember) return false;
      const status = getBandStatus(band);
      return status === 'idle';
    });
    
    if (idleBands.length === 0) {
      if (showEmptyMessage) {
        message.info('当前没有空闲状态的手环');
      }
      return;
    }
    
    // 批量隐藏
    let successCount = 0;
    let failCount = 0;
    
    for (const band of idleBands) {
      try {
        // 解析当前remark
        let remarkData = {};
        if (band.remark) {
          try {
            remarkData = JSON.parse(band.remark);
          } catch (e) {
            remarkData = { _originalRemark: band.remark };
          }
        }
        
        // 设置showOnDashboard为false
        remarkData.showOnDashboard = false;
        const newRemark = JSON.stringify(remarkData);
        
        const payload = {
          tableName: 'CUSTOMER_BAND',
          colName: String(selectedCustomerId),
          enumCode: band.enumCode,
          enumName: band.enumName,
          remark: newRemark,
          showSort: band.showSort ?? 0,
          state: 1,
          parentEnumCode: 0,
          configId: band.configId
        };
        
        // 发送请求
        const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/save', payload);
        if (resp.data?.code === 200) {
          successCount++;
          // 更新本地状态
          setBands(prevBands => 
            prevBands.map(b => 
              b.configId === band.configId 
                ? { ...b, showOnDashboard: false, remark: newRemark }
                : b
            )
          );
          // 从激活缓存中移除
          activatedBandsRef.current.delete(band.enumCode);
        } else {
          failCount++;
        }
      } catch (err) {
        console.error(`隐藏手环 ${band.enumCode} 失败:`, err);
        failCount++;
      }
    }
    
    if (successCount > 0) {
      message.success(`成功隐藏 ${successCount} 个空闲手环`);
    }
    if (failCount > 0) {
      message.error(`${failCount} 个手环隐藏失败`);
    }
  }, [apiClient, selectedCustomerId, getBandStatus]);

  // 渲染状态指示器
  const renderStatusIndicator = (band) => {
    const status = getBandStatus(band);

    return (
      <div style={{ fontSize: '12px', color: status === 'online' ? themes[currentTheme].onlineColor : themes[currentTheme].offlineColor }}>
        {status === 'online' ? '● 在线' : '● 空闲'}
      </div>
    );
  };

  // 渲染时间显示
  const renderTimestampDisplay = (band) => {
    const realtimeData = getBandRealtimeData(band.enumCode);
    const currentTime = Date.now();
    
    // 从缓存获取上次的时间数据
    const timeCacheKey = `time_${band.enumCode}`;
    const cachedTimeData = onlineStatusCache[timeCacheKey] || {};
    
    let displayTimestamp = null;
    
    if (realtimeData?.timestamp) {
      // 有实时时间数据
      displayTimestamp = realtimeData.timestamp;
    } else if (cachedTimeData.lastTimestamp && cachedTimeData.lastTimeUpdateTime) {
      // 检查缓存时间是否在3分钟内
      const timeSinceLastOnline = currentTime - cachedTimeData.lastOnlineTime;
      if (timeSinceLastOnline < 180000) { // 3分钟缓冲期
        displayTimestamp = cachedTimeData.lastTimestamp;
      }
    }
    
    if (displayTimestamp) {
      const date = new Date(displayTimestamp);
      return date.toLocaleTimeString();
    }
    return '--:--:--';
  };

  // 手环卡片样式 - 固定宽度，由BandCardGrid组件控制
  const getCardBaseStyle = () => ({
    height: 'auto',
    minHeight: '200px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  });

  // 渲染单个手环卡片
  const renderBandCard = (band, index) => {
    // 获取课程数据
    const courses = coursesCache[band.boundMember?.id] || [];
    const hasCourses = showCourses && courses.length > 0;

    return (
      <Card
        key={band.configId || index}
        className="venue-dashboard-card"
        style={{
          ...getCardBaseStyle(),
          backgroundColor: themes[currentTheme].cardBackground,
          border: `1px solid ${themes[currentTheme].cardBorder}`
        }}
      >
        {/* 状态 + 采集时间 - 右上角，通过z-index和布局调整避免盖住会员名字 */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          fontSize: '11px',
          color: themes[currentTheme].textMuted,
          backgroundColor: currentTheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)',
          padding: '2px 6px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 10,
          maxWidth: '80px',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {renderStatusIndicator(band)}
        </div>

        {/* 左侧：头像和姓名 - 点击区域（个人专属手环禁止点击） */}
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '12px',
            flex: 1,
            cursor: band.belong === 'person' ? 'default' : 'pointer',
            color: themes[currentTheme].textPrimary,
            minWidth: 0, // 关键：允许flex子项收缩
            overflow: 'hidden'
          }}
          onClick={() => {
            // 个人专属手环禁止点击切换
            if (band.belong === 'person') {
              message.info('个人专属手环不能切换用户');
              return;
            }
            handleCardClick(band);
          }}
        >
          {band.boundMember ? (
            <>
              <img 
                src={band.boundMember.photoAddress || '/default-avatar.png'} 
                alt={band.boundMember.babyName}
                style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '50%', 
                  marginRight: '12px',
                  objectFit: 'cover',
                  border: `2px solid ${themes[currentTheme].cardBorder}`
                }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4=';
                }}
              />
              <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div style={{ 
                  fontSize: 'clamp(12px, 1.2vw, 16px)', 
                  fontWeight: 'bold',
                  color: themes[currentTheme].textPrimary,
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {band.boundMember.babyName}
                </div>
                <div style={{ 
                  fontSize: 'clamp(10px, 1vw, 12px)', 
                  color: themes[currentTheme].textSecondary,
                  marginBottom: '2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {band.enumName}
                </div>
                <div style={{ 
                  fontSize: 'clamp(9px, 0.8vw, 11px)', 
                  color: themes[currentTheme].textMuted,
                  fontFamily: 'monospace',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {band.enumCode}
                </div>
              </div>
            </>
          ) : (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              color: themes[currentTheme].textMuted
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: currentTheme === 'dark' ? '#2a2a2a' : '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                border: `2px dashed ${themes[currentTheme].cardBorder}`
              }}>
                <span style={{ fontSize: '24px', color: themes[currentTheme].textMuted }}>?</span>
              </div>
              <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <div style={{ 
                  fontSize: 'clamp(12px, 1.2vw, 16px)', 
                  fontWeight: 'bold',
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  未绑定
                </div>
                <div style={{ 
                  fontSize: 'clamp(10px, 1vw, 12px)',
                  color: themes[currentTheme].textSecondary,
                  marginBottom: '2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {band.enumName}
                </div>
                <div style={{ 
                  fontSize: 'clamp(9px, 0.8vw, 11px)', 
                  color: themes[currentTheme].textMuted,
                  fontFamily: 'monospace',
                  letterSpacing: '0.5px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {band.enumCode}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 数据展示区域 */}
        <div style={{
          borderTop: `1px solid ${themes[currentTheme].cardBorder}`,
          paddingTop: '12px',
          fontSize: '12px',
          color: themes[currentTheme].textSecondary
        }}>
          {(() => {
            const realtimeData = getBandRealtimeData(band.enumCode);
            const currentTime = Date.now();
            
            // 从缓存获取上次的数据
            const cacheKey = `data_${band.enumCode}`;
            const cachedData = onlineStatusCache[cacheKey] || {};
            
            // 判断是否使用缓存数据
            let displayData = {};
            
            if (realtimeData) {
              // 有实时数据，使用实时数据
              displayData = realtimeData;
            } else if (cachedData.lastDataUpdateTime) {
              // 没有实时数据，检查缓存是否在3分钟内
              const timeSinceLastData = currentTime - cachedData.lastDataUpdateTime;
              if (timeSinceLastData < 180000) { // 3分钟缓冲期
                displayData = cachedData;
              }
            }
            
            // 判断当前状态：如果有实时心率数据，则是在线状态；否则是空闲状态
            const isOnline = realtimeData?.heart_rate > 0;
            
            // 如果在线且有有效数据，记录到 ref 中（避免在渲染中调用 setState）
            if (isOnline && displayData.heart_rate > 0) {
              pendingLastOnlineDataRef.current[band.enumCode] = { ...displayData };
            }
            
            // 获取手环最后在线时的数据（用于课后统计）
            const lastOnlineData = bandStatuses[band.enumCode]?.lastOnlineData || {};
            
            // 确定要显示的统计数据（卡路里、最大心率、有效时长）
            // 在线时使用实时数据，空闲时使用最后在线时的数据
            const statsData = isOnline ? displayData : (lastOnlineData.heart_rate > 0 ? lastOnlineData : displayData);
            
            return (
              <div className="band-stats-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ width: 'calc(50% - 4px)' }}>
                  <div style={{ fontWeight: 'bold', color: themes[currentTheme].textPrimary }}>卡路里</div>
                  <div>{statsData?.calories ? `${Math.round(statsData.calories)} kcal` : '-- kcal'}</div>
                </div>
                <div style={{ width: 'calc(50% - 4px)' }}>
                  <div style={{ fontWeight: 'bold', color: themes[currentTheme].textPrimary }}>实时心率</div>
                  <div>
                    {displayData?.heart_rate ? (
                      <span style={{
                        color: displayData.heart_rate < 120 ? themes[currentTheme].heartRateLow :
                               displayData.heart_rate <= 200 ? themes[currentTheme].heartRateNormal : themes[currentTheme].heartRateHigh,
                        fontWeight: 'bold'
                      }}>
                        {displayData.heart_rate} bpm
                      </span>
                    ) : '-- bpm'}
                  </div>
                </div>
                <div style={{ width: 'calc(50% - 4px)' }}>
                  <div style={{ fontWeight: 'bold', color: themes[currentTheme].textPrimary }}>最大心率</div>
                  <div>{statsData?.max_heart_rate ? `${statsData.max_heart_rate} bpm` : '-- bpm'}</div>
                </div>
                <div style={{ width: 'calc(50% - 4px)' }}>
                  <div style={{ fontWeight: 'bold', color: themes[currentTheme].textPrimary }}>有效时长</div>
                  <div>
                    {statsData?.exercise_duration ? 
                      (() => {
                        const seconds = statsData.exercise_duration;
                        const hours = Math.floor(seconds / 3600);
                        const minutes = Math.floor((seconds % 3600) / 60);
                        const secs = seconds % 60;
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                      })() : '--:--:--'}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* 课程信息区域 */}
        {hasCourses && (
          <>
            <div style={{
              borderTop: `1px solid ${themes[currentTheme].cardBorder}`,
              marginTop: '12px',
              paddingTop: '12px',
              fontSize: '12px',
              color: themes[currentTheme].textSecondary
            }}>
              <div style={{ fontWeight: 'bold', color: themes[currentTheme].textPrimary, marginBottom: '8px' }}>
                {(() => {
                  // 从缓存中获取该会员的所有课程时间点
                  const memberCourses = coursesCache[band.boundMember?.id] || [];
                  if (memberCourses.length > 0) {
                    // 提取所有不同的时间点并去重
                    const uniqueTimes = [...new Set(
                      memberCourses
                        .filter(course => course.trainDate)
                        .map(course => dayjs(course.trainDate).format('HH:mm'))
                    )];
                    
                    if (uniqueTimes.length > 0) {
                      // 如果只有一个时间点，显示"本日课程(09:30)"
                      // 如果有多个时间点，显示"本日课程(09:30,10:00)"
                      return `本日课程(${uniqueTimes.join(',')})`;
                    }
                  }
                  return '本日课程';
                })()}
              </div>
              <div>
                {(() => {
                  let itemIndex = 1;
                  return courses.map((course, courseIndex) => (
                    <div key={course.id} style={{ marginBottom: '4px' }}>
                      {course.details?.map((detail, detailIndex) => (
                        <div key={detail.id} style={{ color: themes[currentTheme].textPrimary }}>
                          {itemIndex++}. {detail.sportsItem}
                        </div>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            </div>
          </>
        )}

        {/* 隐藏按钮 - 在空闲状态时显示在右下角 */}
        {(() => {
          // 使用与状态指示器相同的逻辑判断空闲状态，保持缓冲期一致
          const status = getBandStatus(band);
          const isIdle = status === 'idle';
          
          if (isIdle) {
            return (
              <div style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                zIndex: 10
              }}>
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHideBand(band);
                  }}
                  style={{
                    color: themes[currentTheme].textMuted,
                    fontSize: '11px',
                    padding: '0 4px'
                  }}
                >
                  隐藏
                </Button>
              </div>
            );
          }
          return null;
        })()}
      </Card>
    );
  };

  // 根据手环编号查找对应的实时数据
  const getBandRealtimeData = (bandEnumCode) => {
    if (!mqttData || !Array.isArray(mqttData.devices)) return null;
    
    // 匹配手环编号（mac字段）
    if (!Array.isArray(mqttData.devices)) return null;
    return mqttData.devices.find(device => {
      // 精确匹配手环编号（mac）
      if (device.device_id === bandEnumCode) return true;
      
      // 兼容性匹配：如果bandEnumCode是完整的mac地址
      if (device.device_id && bandEnumCode && 
          device.device_id.toLowerCase() === bandEnumCode.toLowerCase()) {
        return true;
      }
      
      return false;
    }) || null;
  };
  
  // 心率颜色渲染
  const renderHr = (hr) => {
    let color = '#333';
    if (hr < 120) color = themes[currentTheme].heartRateLow;
    else if (hr <= 200) color = themes[currentTheme].heartRateNormal;
    else color = themes[currentTheme].heartRateHigh;
    return <span style={{ color, fontWeight: 'bold' }}>{hr}</span>;
  };

  // 时间格式化
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    return date.toLocaleTimeString();
  };

  // 秒转分钟
  const formatMinutes = (seconds) => {
    if (!seconds) return '0';
    return Math.floor(seconds / 60) + '分';
  };

  // 将 ref 中的状态变化同步到状态中
  useEffect(() => {
    const syncStatusChanges = () => {
      const changes = statusChangeRef.current;
      const bandCodes = Object.keys(changes);
      
      if (bandCodes.length > 0) {
        setBandStatuses(prev => {
          const newStatuses = { ...prev };
          bandCodes.forEach(bandCode => {
            const change = changes[bandCode];
            // 只有在有绑定会员时才保留最后在线数据
            if (change.newStatus === 'idle' && change.hasBoundMember) {
              // 从 pending ref 中获取最后在线数据
              const lastData = pendingLastOnlineDataRef.current[bandCode];
              if (lastData) {
                newStatuses[bandCode] = {
                  ...newStatuses[bandCode],
                  status: change.newStatus,
                  lastOnlineData: lastData
                };
              } else {
                newStatuses[bandCode] = {
                  ...newStatuses[bandCode],
                  status: change.newStatus
                };
              }
            } else if (change.newStatus === 'online') {
              newStatuses[bandCode] = {
                ...newStatuses[bandCode],
                status: change.newStatus
              };
            }
          });
          return newStatuses;
        });
        // 清空 ref
        statusChangeRef.current = {};
      }
    };

    const timer = setInterval(syncStatusChanges, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div 
      ref={dashboardRef}
      style={{ 
        padding: '16px',
        backgroundColor: themes[currentTheme].backgroundColor,
        minHeight: '100vh'
      }}
    >
      {/* 顶部工具栏 - 齿轮图标聚合方式 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* 校区名称 - 点击打开校区选择器（多校区用户可点击） */}
          <h2
            style={{
              margin: 0,
              color: themes[currentTheme].textPrimary,
              cursor: !isFullscreen && customers.length > 1 ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (!isFullscreen && customers.length > 1) {
                setCustomerSelectorVisible(true);
              }
            }}
            title={!isFullscreen && customers.length > 1 ? '点击切换校区' : ''}
          >
            {selectedCustomerName}
          </h2>
          {/* 连接状态 - 校区名称右边 */}
          <div style={{
            fontSize: '12px',
            color: mqttConnected ? themes[currentTheme].onlineColor : themes[currentTheme].textMuted,
            backgroundColor: currentTheme === 'cyber' ? 'rgba(10, 25, 41, 0.8)' : (currentTheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.9)'),
            padding: '4px 10px',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            {mqttConnected ? <CheckCircleOutlined /> : <DisconnectOutlined />}
            {mqttConnected ? '已连接' : '未连接'}
          </div>
        </div>
        
        {/* 右侧：齿轮图标 + 条件显示的功能按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* 功能按钮区域 - 由齿轮图标控制显示/隐藏 */}
          {showToolbar && (
            <div style={{ 
              display: 'flex', 
              gap: '12px',
              padding: '10px 16px',
              backgroundColor: currentTheme === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.95)',
              borderRadius: '10px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
              animation: 'slideIn 0.2s ease'
            }}>
              {/* 刷新按钮 */}
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  fetchBands();
                  message.success('页面已刷新');
                }}
                size="small"
                title="刷新"
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #d9d9d9',
                  color: '#333'
                }}
              >
                刷新
              </Button>
              
              {/* 展示/隐藏课程按钮 */}
              <Button
                onClick={async () => {
                  const newShowCourses = !showCourses;
                  setShowCourses(newShowCourses);
                  // 保存到枚举（先删除再保存）
                  await saveBandTVConfig('venueDashboardShowCourses', String(newShowCourses));
                  if (newShowCourses) {
                    bands.forEach(band => {
                      if (band.boundMember?.id) {
                        updateCoursesCache(band.boundMember.id);
                      }
                    });
                  }
                }}
                size="small"
                style={{
                  backgroundColor: showCourses ? '#1890ff' : '#fff',
                  border: showCourses ? '1px solid #1890ff' : '1px solid #d9d9d9',
                  color: showCourses ? '#fff' : '#333'
                }}
              >
                {showCourses ? '隐藏课程' : '展示课程'}
              </Button>
              
              {/* 主题切换按钮 */}
              {!isFullscreen && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowThemeDropdown(!showThemeDropdown);
                    }}
                    size="small"
                    style={{
                      backgroundColor: '#fff',
                      border: '1px solid #d9d9d9',
                      color: '#333'
                    }}
                  >
                    {themes[currentTheme]?.name || '默认主题'}
                  </Button>
                  {showThemeDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 9999,
                        minWidth: '120px',
                      }}
                    >
                      {Object.entries(themes).map(([key, themeItem]) => (
                        <div
                          key={key}
                          onClick={async () => {
                            setCurrentTheme(key);
                            // 保存到枚举（先删除再保存）
                            await saveBandTVConfig('venueDashboardTheme', key);
                            setShowThemeDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: currentTheme === key ? '#f5f5f5' : '#fff',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = currentTheme === key ? '#f5f5f5' : '#fff'}
                        >
                          {themeItem.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* 全屏按钮 */}
              <Button
                onClick={toggleFullscreen}
                size="small"
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #d9d9d9',
                  color: '#333'
                }}
              >
                {isFullscreen ? '退出全屏' : '全屏展示'}
              </Button>
              
              {/* 维护按钮 */}
              {!isFullscreen && (
                <Button
                  onClick={() => setShowMaintenanceModal(true)}
                  size="small"
                  style={{
                    backgroundColor: '#1890ff',
                    border: '1px solid #1890ff',
                    color: '#fff'
                  }}
                >
                  维护
                </Button>
              )}

            </div>
          )}
          
          {/* 齿轮图标 - 控制工具栏显示/隐藏 */}
          <Button
            icon={<SettingOutlined />}
            onClick={() => setShowToolbar(!showToolbar)}
            shape="circle"
            size="large"
            style={{
              backgroundColor: showToolbar ? '#1890ff' : '#fff',
              border: showToolbar ? '2px solid #1890ff' : '2px solid #d9d9d9',
              color: showToolbar ? '#fff' : '#333',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'all 0.2s ease'
            }}
          />
        </div>
      </div>

      {/* 手环展示区域 */}
      {loading ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px' 
        }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px', color: themes[currentTheme].textSecondary }}>正在加载手环数据...</div>
        </div>
      ) : bands.length === 0 ? (
        <Empty 
          description={<span style={{ color: themes[currentTheme].textMuted }}>暂无手环数据</span>}
          style={{ padding: '60px' }}
        >
          <p style={{ color: themes[currentTheme].textMuted }}>请先添加手环设备</p>
        </Empty>
      ) : (
        <div>
          {/* 提示信息 */}
          {/* 💡 <strong>提示：</strong>
            页面右上角的调试面板会实时显示接收到的MQTT数据。
            手环卡片中的数据会根据接收到的实时数据自动更新。 */}

          <BandCardGrid gap={10} minCardWidth={170} maxCardWidth={260}>
            {bands.filter(band => {
              // 只显示showOnDashboard为true且绑定了会员的手环
              if (band.showOnDashboard === false) return false;
              if (!band.boundMember) return false;
              return true;
            }).map((band, index) => renderBandCard(band, index))}
          </BandCardGrid>
        </div>
      )}

      {/* 手环绑定弹窗 */}
      <Modal
        title={bindMode === 'switch' ? '切换会员' : '绑定会员'}
        open={bindModalVisible}
        onOk={handleSaveBind}
        onCancel={handleCancelBind}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={bindForm} layout="vertical">
          <Form.Item
            label="手环编号"
            name="enumCode"
          >
            <Input placeholder="手环编号" disabled />
          </Form.Item>
          <Form.Item
            label="手环昵称"
            name="enumName"
            rules={[{ required: true, message: '请输入手环昵称' }]}
          >
            <AutoComplete
              id="band-nickname-input"
              options={bandNickNameOptions}
              placeholder="请输入或选择手环昵称"
              allowClear
              value={bandNickNameInput}
              onChange={(value) => {
                setBandNickNameInput(value);
                bindForm.setFieldsValue({ enumName: value });
              }}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
          {/* 快速选择标签 - 仅在输入为空时显示 */}
          {(!bandNickNameInput || bandNickNameInput.trim() === '') && bandNickNameOptions.length > 0 && (
            <div style={{ marginTop: '-8px', marginBottom: '16px' }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#999', 
                marginBottom: '8px' 
              }}>
                快速选择：
              </div>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px' 
              }}>
                {bandNickNameOptions.map((option, index) => {
                  // 两种颜色轮询：蓝色和绿色
                  const isBlue = index % 2 === 0;
                  return (
                    <div
                      key={option.value}
                      onClick={() => {
                        setBandNickNameInput(option.label);
                        bindForm.setFieldsValue({ enumName: option.label });
                      }}
                      style={{
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        border: `1px solid ${isBlue ? '#1890ff' : '#52c41a'}`,
                        backgroundColor: isBlue ? '#e6f7ff' : '#f6ffed',
                        color: isBlue ? '#096dd9' : '#389e0d',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '0.8';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '1';
                      }}
                    >
                      {option.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <Form.Item 
            label={bindMode === 'switch' ? '新会员' : '绑定会员'} 
            name="remark"
            rules={[{ required: bindMode === 'bind', message: bindMode === 'bind' ? '请选择要绑定的会员' : '请选择新会员' }]}
          >
            <div>
              {selectedMember ? (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                  <img 
                    src={selectedMember.photoAddress} 
                    alt={selectedMember.babyName}
                    style={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      marginRight: 12,
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPk1lbWJlcjwvdGV4dD48L3N2Zz4=';
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{selectedMember.babyName}</div>
                    <div style={{ fontSize: '12px', color: themes[currentTheme].textSecondary }}>ID: {selectedMember.id}</div>
                  </div>
                  {bindMode === 'switch' && (
                    <Button 
                      type="text" 
                      danger 
                      size="small" 
                      onClick={() => handleUnbindBand(currentBand)}
                      style={{
                        color: '#ff4d4f',
                        backgroundColor: 'rgba(255, 77, 79, 0.1)',
                        border: '1px solid #ff4d4f',
                        borderRadius: '4px',
                        padding: '0 8px',
                        height: '24px',
                        lineHeight: '22px',
                        marginLeft: 'auto'
                      }}
                    >
                      取消绑定
                    </Button>
                  )}
                  {bindMode === 'bind' && (
                    <Button 
                      type="text" 
                      size="small" 
                      onClick={() => {
                        setSelectedMember(null);
                        bindForm.setFieldsValue({ remark: '' });
                      }}
                      style={{
                        color: themes[currentTheme].textSecondary,
                        backgroundColor: currentTheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                        border: `1px solid ${themes[currentTheme].cardBorder}`,
                        borderRadius: '4px',
                        padding: '0 8px',
                        height: '24px',
                        lineHeight: '22px',
                        marginLeft: 'auto'
                      }}
                    >
                      取消
                    </Button>
                  )}
                </div>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  <span style={{ color: themes[currentTheme].textMuted, fontSize: '12px' }}>请选择要绑定的会员：</span>
                </div>
              )}
              <MemberSelector 
                onSelect={handleMemberSelect}
                selectedMemberIds={selectedMember ? [selectedMember.id] : []}
              />
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 校区选择弹窗 */}
      <Modal
        title="选择校区"
        open={customerSelectorVisible}
        onOk={() => setCustomerSelectorVisible(false)}
        onCancel={() => setCustomerSelectorVisible(false)}
        okText="确定"
        cancelText="取消"
        destroyOnClose
      >
        <div style={{ padding: '16px 0' }}>
          <CustomerSelector
            value={selectedCustomerId}
            onChange={(customerId) => {
              // 查找对应的校区名称
              const customer = customers.find(c => c.customerId === customerId);
              const customerName = customer ? customer.customerName : '校区运动手环';
              handleCustomerChange(customerId, customerName);
              setCustomerSelectorVisible(false);
            }}
            placeholder="请选择校区"
            showTeamSelector={false}
            style={{ width: '100%' }}
          />
        </div>
      </Modal>
      
      {/* 维护弹窗 - 使用 CustomerBandsViewer 组件 */}
      <Modal
        title="手环维护"
        open={showMaintenanceModal}
        onCancel={() => setShowMaintenanceModal(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <CustomerBandsViewer
          autoOpen={true}
          onBandsChange={() => {
            fetchBands();
            setShowMaintenanceModal(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default VenueDashboard;

